import {
  Box,
  Grid,
  IconButton,
  Link,
  styled as muiStyled,
  Typography,
} from "@mui/material";
import type React from "react";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import Slider, { type Settings } from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { CovTag } from "@cx-component-library/react-components";
import styled from "@emotion/styled";
import { ImageCarouselDialog } from "components/dialog";
import {
  SettingsContext,
  type SettingsContextValue,
} from "providers/SettingsProvider";
import ArrowLeftIcon from "../../assets/bgImgCarousel/arrow_left.svg";
import ArrowRightIcon from "../../assets/bgImgCarousel/arrow_right.svg";
import PlayArrowIcon from "../../assets/bgImgCarousel/play.svg";
import PauseIcon from "../../assets/bgImgCarousel/stop.svg";

export interface ImageCarouselAttribute {
  src: string;
  label: string;
  title: string;
  text: string;
  textMore: string;
  bgColor: string;
}
interface BgImagesCarouselProps {
  images: ImageCarouselAttribute[];
  configs?: Settings;
  goToSlide?: number;
  showMore?: boolean;
  onChangeMore?: (showMore: boolean) => void;
  t: (key: string) => string;
  onChangeIndex: (index: number) => void;
}

const BoxTopContentImage = muiStyled(Box)(({ theme }) => ({
  backgroundPosition: "center center",
  backgroundRepeat: "no-repeat",
  backgroundSize: "cover",
  position: "relative",
  height: "320px",
  width: "100%",
  [theme.breakpoints.up("sm")]: {
    height: "420px",
  },
  [theme.breakpoints.up("md")]: {
    height: "539px",
  },
}));

const IconButtonWrapped = muiStyled(IconButton)(() => ({
  width: "48px",
  height: "48px",
  backgroundColor: "hsla(0,0%,100%,.15)",
  "&:hover": {
    backgroundColor: "hsla(0,0%,100%,.15)",
  },
  borderRadius: "48px",
  color: "#fff",
  fontWeight: 400,
}));

const BoxTopContentInside = muiStyled(Box)(({ theme }) => ({
  width: "100%",
  position: "absolute",
  background: "linear-gradient(rgba(0, 0, 0, 0) 1.86%, #000000 40.72%)",
  left: 0,
  bottom: "-42px",
  paddingTop: "32px",
  paddingLeft: "16px",
  paddingRight: "16px",
  paddingBottom: "72px",
  flexShrink: 0,
  overflow: "hidden",
  transition: "height 300ms",
  [theme.breakpoints.up("sm")]: {
    paddingTop: "48px",
    paddingLeft: "32px",
    paddingRight: "32px",
    paddingBottom: "96px",
  },
  [theme.breakpoints.up("md")]: {
    paddingTop: "65px",
    paddingLeft: "96px",
    paddingRight: "96px",
    paddingBottom: "123px",
  },
}));

const BgImageTitle = styled.h5({
  color: "#fff",
  fontSize: "clamp(18px, 4vw, 24px)",
  fontWeight: 700,
  letterSpacing: "0px",
  lineHeight: "1.3",
  marginBottom: 0,
  marginTop: "8px",
  fontStyle: "normal",
  textShadow: "0 0 24px rgba(0,0,0,.5)",
  maxWidth: "800px",
});

const BgImageText = styled.h5({
  color: "#D9D9D9",
  maxWidth: "800px",
  fontFamily: 'var(--font-family, "AktivGrotesk")',
  fontSize: "var(--body-b2-14-regular-size, 14px)",
  fontWeight: 400,
  letterSpacing: "0.25px",
  lineHeight: "20px",
  fontStyle: "normal",
  marginBottom: 0,
  marginTop: 0,
});

const BgCarouselMoveButton = styled.img({
  width: "12px",
  height: "22.6px",
});

const BgCarouselStateButton = styled.img({
  width: "24px",
  height: "24px",
  color: "#fff",
});

export const BgImagesCarousel: React.FC<BgImagesCarouselProps> = ({
  images,
  configs,
  goToSlide,
  showMore = false,
  t,
  onChangeIndex,
}) => {
  const sliderRef = useRef<Slider | null>(null);
  const [isPlaying, setIsPlaying] = useState(true);
  const { settings } = useContext<SettingsContextValue>(SettingsContext);
  const [manualClick, setManualClick] = useState<boolean>(false);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedSlide, setSelectedSlide] =
    useState<ImageCarouselAttribute | null>(null);

  const defaultSettings: Settings = {
    dots: false,
    arrows: false,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: false, // autoplay will handle with requestAnimationFrame
    // autoplaySpeed: 5000, // default: 5 seconds
    // biome-ignore lint/correctness/noUnusedFunctionParameters: ToBeEvaluated with the team
    beforeChange: (oldIndex, newIndex) => {
      setCurrentSlide(newIndex);
      onChangeIndex(newIndex);
      startTimeRef.current = 0;
      setProgress(0);
    },
    ...configs,
  };

  const [currentSlide, setCurrentSlide] = useState(0);
  const [progress, setProgress] = useState(0);
  const requestRef = useRef<number>();
  const startTimeRef = useRef<number>(0);

  const DURATION = 5000; // 5 giây

  const onChangeMore = useCallback((param: boolean) => {
    setIsOpen(param);
    if (param) {
      setIsPlaying(false);
    }
  }, []);

  const animate = useCallback(
    (timestamp: number) => {
      if (!startTimeRef.current) startTimeRef.current = timestamp;
      const elapsed = timestamp - startTimeRef.current;
      let newProgress = elapsed / DURATION;

      if (newProgress >= 1) {
        newProgress = 0;
        startTimeRef.current = timestamp; // reset time
        sliderRef.current?.slickNext(); // endtime of each circle -> move next
        onChangeMore(false);
      }

      setProgress(newProgress);
      requestRef.current = requestAnimationFrame(animate);
    },
    [onChangeMore],
  );

  // tracking animation
  useEffect(() => {
    if (isPlaying) {
      requestRef.current = requestAnimationFrame(animate);
    } else {
      if (requestRef.current) cancelAnimationFrame(requestRef.current);
    }
    return () => {
      if (requestRef.current) cancelAnimationFrame(requestRef.current);
    };
  }, [animate, isPlaying]);

  useEffect(() => {
    if (settings.inputTypingSearch) {
      setIsPlaying(!settings.inputTypingSearch);
    } else {
      if (!manualClick) setIsPlaying(true);
    }
  }, [settings, manualClick]);

  // circle canculated at here
  const size = 60; // SVG size
  const strokeWidth = 4;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference * (1 - progress);

  const nextImg = () => {
    sliderRef.current?.slickNext();
    onChangeMore(false);
  };

  const prevImg = () => {
    sliderRef.current?.slickPrev();
    onChangeMore(false);
  };

  useEffect(() => {
    if (goToSlide !== undefined) {
      sliderRef.current?.slickGoTo(goToSlide);
    }
  }, [goToSlide]);

  return (
    <Grid sx={{ position: "relative", width: "100%", overflow: "hidden" }}>
      <Slider
        ref={(slider) => (sliderRef.current = slider)}
        {...defaultSettings}
      >
        {images.map((image) => (
          <BoxTopContentImage
            sx={{ backgroundImage: `url(${image.src})` }}
            key={"ContentTopImage"}
            data-cy={"ContentTopImage"}
          >
            <BoxTopContentInside
              sx={{ position: `${showMore ? "relative" : "absolute"}` }}
            >
              <CovTag
                sx={{
                  backgroundColor: `${image.bgColor} !important`,
                  padding: "5px 12px 7px",
                  ".MuiChip-label": {
                    padding: 0,
                    fontSize: "0.75rem",
                    letterSpacing: "0.8px",
                  },
                }}
                label={t(image.label).toLocaleUpperCase()}
              />
              <BgImageTitle
                id="DIGITAL_SAMPLING_IMAGIO"
                data-cy="DIGITAL_SAMPLING_IMAGIO"
              >
                {t(image.title)}
              </BgImageTitle>
              <BgImageText
                id="DIGITAL_SAMPLING_CONTENT_TEXT"
                data-cy="DIGITAL_SAMPLING_CONTENT_TEXT"
              >
                <Typography
                  sx={{
                    fontFamily: 'var(--font-family, "AktivGrotesk")',
                    fontWeight: 400,
                    fontSize: "var(--body-b2-14-regular-size, 14px)",
                    float: "left",
                  }}
                >
                  {t(image.text)}&nbsp;&nbsp;
                  {!showMore && (
                    <Link
                      component="button"
                      onClick={() => {
                        onChangeMore(true);
                        setSelectedSlide(image);
                      }}
                      sx={{
                        "&:hover": { textDecoration: "none" },
                        textDecoration: "none",
                      }}
                    >
                      {" "}
                      {t("ldp_read_more")}
                    </Link>
                  )}
                </Typography>
              </BgImageText>
            </BoxTopContentInside>
          </BoxTopContentImage>
        ))}
      </Slider>

      <Box
        sx={{
          position: "absolute",
          bottom: { sm: "80px", md: "110px" },
          right: { sm: "24px", md: "87px" },
          display: { xs: "none", sm: "flex" },
          gap: 1,
          background: "inherit",
          borderRadius: 2,
          p: 1,
        }}
      >
        <IconButtonWrapped sx={{ marginTop: "6px" }} onClick={prevImg}>
          <BgCarouselMoveButton src={ArrowLeftIcon} />
        </IconButtonWrapped>
        <Box position="relative" width={60} height={60}>
          {/** biome-ignore lint/a11y/noSvgWithoutTitle: ToBeEvaluated with the team */}
          <svg width="60" height="60">
            <circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              stroke={images[currentSlide]?.bgColor}
              strokeWidth={strokeWidth}
              fill="none"
              strokeDasharray={circumference}
              strokeDashoffset={strokeDashoffset}
              style={{
                transition: isPlaying ? "stroke-dashoffset 0s linear" : "none",
                transform: "rotate(-90deg)",
                transformOrigin: "center",
              }}
            />
          </svg>

          <IconButtonWrapped
            onClick={() => {
              setIsPlaying(!isPlaying);
              setManualClick(!manualClick);
              startTimeRef.current = 0; // will reset the timer when click on
            }}
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              zIndex: 1,
            }}
          >
            {isPlaying ? (
              <BgCarouselStateButton src={PlayArrowIcon} />
            ) : (
              <BgCarouselStateButton
                style={{ marginLeft: "4px" }}
                src={PauseIcon}
              />
            )}
          </IconButtonWrapped>
        </Box>
        <IconButtonWrapped sx={{ marginTop: "6px" }} onClick={nextImg}>
          <BgCarouselMoveButton src={ArrowRightIcon} />
        </IconButtonWrapped>
      </Box>

      <ImageCarouselDialog
        open={isOpen}
        onClose={() => {
          onChangeMore(false);
          setIsPlaying(true);
        }}
        image={selectedSlide?.src}
        label={selectedSlide?.label}
        title={selectedSlide?.title}
        textMore={selectedSlide?.textMore}
        bgColor={selectedSlide?.bgColor}
      />
    </Grid>
  );
};
